import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { PRD5010_R01Dto, PRD5010_R01ProductResultDto } from './Dto_PRD5010_R01';
import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';

export interface PRD5010_R01Filter {
    compCd: string;
    kpicertkey: string;
    ocrdttmDateRange: DateRange | undefined;
    kpifldcd: string;
}

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_PRD5010_R01 {
    PRD5010_R01Filter: PRD5010_R01Filter;
    PRD5010_R01PastFilter: PRD5010_R01Filter;
    setPRD5010_R01Filter: (filter: Partial<PRD5010_R01Filter>) => void;
    setPRD5010_R01PastFilter: (filter: Partial<PRD5010_R01Filter>) => void;

    // === 리스트 관련 ===
    PRD5010_R01List: PRD5010_R01Dto[];
    setPRD5010_R01List: (list: PRD5010_R01Dto[]) => void;
    insertPRD5010_R01List: (data: PRD5010_R01Dto[], index?: number) => void;
    removePRD5010_R01List: (index: number) => void;
    updatePRD5010_R01List: (data: PRD5010_R01Dto, index: number) => void;
    
    // === 선택 관련 ===
    selectedPRD5010_R01List: string[];
    setSelectedPRD5010_R01List: (idList: string[]) => void;
    addSelectedPRD5010_R01List: (idList: string[]) => void;
    removeSelectedPRD5010_R01List: (idList?: string[]) => void;
    initSelectedPRD5010_R01List: () => void;

	
    // === 생산실적 리스트 관련 ===
    PRD5010_R01ProductResultList: PRD5010_R01ProductResultDto[];
    setPRD5010_R01ProductResultList: (list: PRD5010_R01ProductResultDto[]) => void;
    insertPRD5010_R01ProductResultList: (data: PRD5010_R01ProductResultDto[], index?: number) => void;
    removePRD5010_R01ProductResultList: (index: number) => void;
    updatePRD5010_R01ProductResultList: (data: PRD5010_R01ProductResultDto, index: number) => void;
    
    // === 생산실적 선택 관련 ===
    selectedPRD5010_R01ProductResultList: string[];
    setSelectedPRD5010_R01ProductResultList: (idList: string[]) => void;
    addSelectedPRD5010_R01ProductResultList: (idList: string[]) => void;
    removeSelectedPRD5010_R01ProductResultList: (idList?: string[]) => void;
    initSelectedPRD5010_R01ProductResultList: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;
    productResultColumnState: ColumnState[] | null;
    setProductResultColumnState: (state: ColumnState[] | null) => void;

    allCount: number;
    setAllCount: (allCount: number) => void;

    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;
    detailScrollTop: number | null;
    setDetailScrollTop: (top: number | null) => void;

    // === 전체 초기화 ===
    initData: () => void;
}

/**
 * 기본 날짜 범위 (현재 월)
 */
const defaultDateRange: DateRange = getCurrentMonthDateRange();

export const defaultPRD5010_R01Filter: PRD5010_R01Filter = {
    compCd: '',
    kpicertkey: '',
    ocrdttmDateRange: defaultDateRange,
    kpifldcd: '',
};

/**
 * Zustand 스토어 정의
 */
export const usePRD5010_R01Store = create<Store_PRD5010_R01>((set) => {
    /**
     * 공통 필터 설정 함수 생성
     */
    const createFilterSetter = (filterKey: keyof Pick<Store_PRD5010_R01, 'PRD5010_R01Filter' | 'PRD5010_R01PastFilter'>) => 
        (filter: Partial<PRD5010_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    /**
     * 선택된 항목 제거 헬퍼 함수
     */
    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        PRD5010_R01Filter: defaultPRD5010_R01Filter,
        PRD5010_R01PastFilter: defaultPRD5010_R01Filter,
        setPRD5010_R01Filter: createFilterSetter('PRD5010_R01Filter'),
        setPRD5010_R01PastFilter: createFilterSetter('PRD5010_R01PastFilter'),

        // === 리스트 관련 ===
        PRD5010_R01List: [],
        setPRD5010_R01List: (list: PRD5010_R01Dto[]) => set({ PRD5010_R01List: list }),
        insertPRD5010_R01List: (data: PRD5010_R01Dto[], index?: number) => set((state) => {
            const _list = [...state.PRD5010_R01List];
            
            if (index !== undefined) {
                _list.splice(index, 0, ...data);
            } else {
                _list.push(...data);
            }
            return { ...state, PRD5010_R01List: _list };
        }),
        removePRD5010_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            PRD5010_R01List: state.PRD5010_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updatePRD5010_R01List: (data: PRD5010_R01Dto, index: number) => set((state) => {
            const _list = [...state.PRD5010_R01List];
            _list[index] = data;
            return { ...state, PRD5010_R01List: _list };
        }),

        // === 선택 관련 ===
        selectedPRD5010_R01List: [],
        setSelectedPRD5010_R01List: (idList: string[]) => set({ selectedPRD5010_R01List: idList }),
        addSelectedPRD5010_R01List: (idList: string[]) => set((state) => {
            const _selectedPRD5010_R01List = [...state.selectedPRD5010_R01List];
            _selectedPRD5010_R01List.push(...idList.filter(value => !_selectedPRD5010_R01List.includes(value)));
            return { ...state, selectedPRD5010_R01List: _selectedPRD5010_R01List };
        }),
        removeSelectedPRD5010_R01List: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedPRD5010_R01List: [] };
            }
            
            return {
                ...state,
                selectedPRD5010_R01List: removeItemsFromArray(state.selectedPRD5010_R01List, idList),
            };
        }),
        initSelectedPRD5010_R01List: () => set({ selectedPRD5010_R01List: [] }),

		

        // === 리스트 관련 ===
        PRD5010_R01ProductResultList: [],
        setPRD5010_R01ProductResultList: (list: PRD5010_R01ProductResultDto[]) => set({ PRD5010_R01ProductResultList: list }),
        insertPRD5010_R01ProductResultList: (data: PRD5010_R01ProductResultDto[], index?: number) => set((state) => {
            const _list = [...state.PRD5010_R01ProductResultList];
            
            if (index !== undefined) {
                _list.splice(index, 0, ...data);
            } else {
                _list.push(...data);
            }
            return { ...state, PRD5010_R01ProductResultList: _list };
        }),
        removePRD5010_R01ProductResultList: (deletedIndex: number) => set((state) => ({
            ...state,
            PRD5010_R01ProductResultList: state.PRD5010_R01ProductResultList.filter((_, index) => index !== deletedIndex),
        })),
        updatePRD5010_R01ProductResultList: (data: PRD5010_R01ProductResultDto, index: number) => set((state) => {
            const _list = [...state.PRD5010_R01ProductResultList];
            _list[index] = data;
            return { ...state, PRD5010_R01ProductResultList: _list };
        }),

        // === 선택 관련 ===
        selectedPRD5010_R01ProductResultList: [],
        setSelectedPRD5010_R01ProductResultList: (idList: string[]) => set({ selectedPRD5010_R01ProductResultList: idList }),
        addSelectedPRD5010_R01ProductResultList: (idList: string[]) => set((state) => {
            const _selectedPRD5010_R01ProductResultList = [...state.selectedPRD5010_R01ProductResultList];
            _selectedPRD5010_R01ProductResultList.push(...idList.filter(value => !_selectedPRD5010_R01ProductResultList.includes(value)));
            return { ...state, selectedPRD5010_R01ProductResultList: _selectedPRD5010_R01ProductResultList };
        }),
        removeSelectedPRD5010_R01ProductResultList: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedPRD5010_R01ProductResultList: [] };
            }
            
            return {
                ...state,
                selectedPRD5010_R01ProductResultList: removeItemsFromArray(state.selectedPRD5010_R01ProductResultList, idList),
            };
        }),
        initSelectedPRD5010_R01ProductResultList: () => set({ selectedPRD5010_R01ProductResultList: [] }),
        
        // === AG Grid 컬럼 상태 관리 ===
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),
        productResultColumnState: null,
        setProductResultColumnState: (state) => set({ productResultColumnState: state }),

        allCount: 0,
		
        setAllCount: (allCount: number) => set({ allCount }),

        scrollTop: null,
        setScrollTop: (top: number | null) => set({ scrollTop: top }),
        detailScrollTop: null,
        setDetailScrollTop: (top: number | null) => set({ detailScrollTop: top }),

        // === 전체 초기화 ===
        initData: () => set(() => ({
            PRD5010_R01Filter: defaultPRD5010_R01Filter,
            PRD5010_R01PastFilter: defaultPRD5010_R01Filter,
            PRD5010_R01List: [],
            PRD5010_R01ProductResultList: [],
            selectedPRD5010_R01List: [],
            selectedPRD5010_R01ProductResultList: [],
            columnState: null,
            productResultColumnState: null,
            allCount: 0,
            scrollTop: null,
            detailScrollTop: null,
		
        })),
    };
});
